import { ModaSofa } from '@dora/backpack';
import { SofaConfigureSeatCount } from './useSofaConfigure/numberOfSeatsFromSize';
import * as Hooks from 'preact/hooks';
import { SeatSize } from './useSofaConfigure/seatSizes';

/**
 * manage the moda sofa configuration form state
 */
export function useFormState(
    defaultShape: ModaSofa.Shape,
    defaultSizes: SofaConfigureSeatCount,
    defaultArms: { left: number; middle: number; right: number },
    defaultCornerStyle: ModaSofa.CornerStyleT,
    placementKind: 'standard' | 'curved',
    seatSizes: SeatSize,
) {
    const [shape, setShape] = Hooks.useState<ModaSofa.Shape>(defaultShape);

    const [seats, setSeats] = Hooks.useState<number>(defaultSizes.straightSeats);
    const [rightSeats, setRightSeats] = Hooks.useState<number>(defaultSizes.rightSeats);
    const [leftSeats, setLeftSeats] = Hooks.useState<number>(defaultSizes.leftSeats);
    const [middleSeats, setMiddleSeats] = Hooks.useState<number>(defaultSizes.middleSeats);

    // TODO: read from UI component
    const [cornerStyle, setCornerStyle] = Hooks.useState<ModaSofa.CornerStyleT>(defaultCornerStyle);

    const [interstitialArms, setInterstitialArms] = Hooks.useState(defaultArms);

    const seatCounts = {
        straight: seats,
        left: leftSeats,
        right: rightSeats,
        middle: middleSeats,
    };

    const interstitials = {
        clear: () => setInterstitialArms({ left: 0, middle: 0, right: 0 }),
        left: {
            set: (count: number) => setInterstitialArms(ia => ({ ...ia, left: count })),
            remove: () => setInterstitialArms(ia => ({ ...ia, left: 0 })),
            count: interstitialArms.left,
            max: leftSeats - seatSizes.endPieceLeft,
        },
        right: {
            set: (count: number) => setInterstitialArms(ia => ({ ...ia, right: count })),
            remove: () => setInterstitialArms(ia => ({ ...ia, right: 0 })),
            count: interstitialArms.right,
            max: rightSeats - seatSizes.endPieceRight,
        },
        middle: {
            set: (count: number) => setInterstitialArms(ia => ({ ...ia, middle: count })),
            remove: () => setInterstitialArms(ia => ({ ...ia, middle: 0 })),
            count: interstitialArms.middle,
            max:
                placementKind === 'curved'
                    ? Math.floor((seats - seatSizes.endPieceLeft - seatSizes.endPieceRight) / 2)
                    : shape === 'straight'
                    ? seats - seatSizes.endPieceLeft - seatSizes.endPieceRight + 1
                    : middleSeats + 1,
        },
    };

    if (interstitials.left.count > interstitials.left.max) {
        interstitials.left.set(interstitials.left.max);
    }
    if (interstitials.right.count > interstitials.right.max) {
        interstitials.right.set(interstitials.right.max);
    }
    if (interstitials.middle.count > interstitials.middle.max) {
        interstitials.middle.set(interstitials.middle.max);
    }

    const handleSetSeats =
        (seatType: 'left' | 'right' | 'middle' | 'straight') => (newSeats: number) => {
            switch (seatType) {
                case 'left':
                    setLeftSeats(newSeats);
                    break;
                case 'right':
                    setRightSeats(newSeats);
                    break;
                case 'middle':
                    setMiddleSeats(newSeats);
                    break;
                case 'straight':
                    setSeats(newSeats);
                    break;
            }
        };

    return {
        shape,
        cornerStyle,
        seatCounts,
        handleSetSeats,
        setShape,
        setCornerStyle,
        interstitials,
    };
}
